import Shared from '../shared.module.css';
import Styles from './styles.module.css';
import React, { useState, useRef, useEffect } from 'react';
import { deleteDoc, doc, getFirestore, updateDoc } from 'firebase/firestore';
import { BsThreeDots } from 'react-icons/bs';
import { IoCalendarOutline, IoLocationOutline, IoPeopleOutline } from 'react-icons/io5';

type SessionCardProps = {
  // メニューの「編集する」
  onEdit: () => void;
  session: {
    id: string;
    name: string;
    description: string;
    schedule: {
      startAt: Date;
      endAt: Date;
      durationMin: number;
    };
    place: string;
    people: number;
    // 受付を締め切ったか(学生アプリでは「受付終了」になり申し込めない)
    closed: boolean;
  };
  // 申込数(キャンセルを除く)
  entryCount: number;
};

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const pad = (n: number) => String(n).padStart(2, '0');

const SessionCard: React.FC<SessionCardProps> = ({ session, onEdit, entryCount }) => {

  const db = getFirestore();

  const [ visible, setVisible ] = useState(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        divRef.current && !divRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleButtonClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setVisible((prev) => !prev);
  };

  const deleteDocument = async () => {
    await deleteDoc(doc(db, 'infoSessions', session.id));
  };

  const date = session.schedule.startAt;
  const isPast = date.getTime() < Date.now();

  // 受付の締め切り・再開。定員に達しても自動では締め切らない(サロンが判断する)。
  // 開催日時を過ぎた説明会は、学生アプリに表示されなくなる。
  const [ toggling, setToggling ] = useState(false);
  const toggleClosed = async () => {
    const next = !session.closed;
    if (next && !window.confirm(`「${session.name}」の受付を締め切りますか？学生アプリでは「受付終了」と表示され、申し込めなくなります。`)) return;
    setToggling(true);
    try {
      await updateDoc(doc(db, 'infoSessions', session.id), { closed: next });
    } catch (e) {
      console.error('受付の切り替えに失敗しました', e);
      window.alert('切り替えられませんでした。時間をおいて、もう一度お試しください。');
    } finally {
      setToggling(false);
    }
  };

  const dateTimeLabel =
    `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 `
    + `${WEEKDAYS[date.getDay()]}曜日 ${pad(date.getHours())}:${pad(date.getMinutes())}~`;

  return (
    <div className={Shared.card}>
      <div className={Styles.head}>
        <h4 className={Styles.title}>{session.name}</h4>
        <span className={isPast || session.closed ? Styles.statusPast : Styles.statusUpcoming}>
          {isPast ? '開催済み' : session.closed ? '受付終了' : '受付中'}
        </span>
      </div>

      {session.description && (
        <p className={Styles.description}>{session.description}</p>
      )}

      <div className={Styles.meta}>
        <div className={Styles.metaRow}>
          <IoCalendarOutline className={Styles.metaIcon} />
          <span>{dateTimeLabel}</span>
        </div>
        <div className={Styles.metaRow}>
          <IoLocationOutline className={Styles.metaIcon} />
          <span>{session.place}</span>
        </div>
        <div className={Styles.metaRow}>
          <IoPeopleOutline className={Styles.metaIcon} />
          <span>申込 {entryCount}名 ／ 定員 {session.people}名</span>
          {session.people > 0 && entryCount >= session.people && <span className={Styles.full}>定員に達しています</span>}
        </div>
      </div>

      {!isPast && (
        <button type="button" className={session.closed ? Styles.reopenBtn : Styles.closeBtn} onClick={toggleClosed} disabled={toggling}>
          {session.closed ? '受付を再開する' : '受付を締め切る'}
        </button>
      )}

      <div className={Shared.cardActions}>
        <div className={Shared.cardMenuBtn} ref={buttonRef} onClick={handleButtonClick}>
          <BsThreeDots />
          {visible && (
            <div className={Shared.cardMenuPopover} ref={divRef}>
              <p onClick={() => { setVisible(false); onEdit(); }}>編集する</p>
              <p onClick={() => deleteDocument()}>削除する</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionCard;
